import React from 'react';
import Hero from '../components/home/Hero';
import AnimatedSection from '../components/ui/AnimatedSection';
import Button from '../components/ui/Button';
import { Star, Shield, Briefcase, Coffee } from 'react-feather';
import PracticeAreaCard from '../components/practice-areas/PracticeAreaCard';
import AuthorityBurst from '../components/funnel/AuthorityBurst';
import Seo from '../components/shared/Seo';
import { generateBreadcrumbSchema, generateFaqSchema } from '../lib/schema';
import FlippableCard from '../components/ui/FlippableCard';
import FaqSection from '../components/blog/FaqSection';
import type { FAQ } from '../types';
import { useData } from '../context/DataContext';
import { useTranslation, Trans } from 'react-i18next';
import LocalizedLink from '../components/ui/LocalizedLink';
import PageExtras from '../components/shared/PageExtras';
import { resolvePracticeAreaPath } from '../lib/utils';


const HomePage: React.FC = () => {
  const { t } = useTranslation('common');
  const { practiceAreas, testimonials, caseResults, firmStats } = useData();
  const breadcrumbSchema = generateBreadcrumbSchema([{ name: 'Home', path: '/' }]);
  const faqs = t('homePage.faqs', { returnObjects: true }) as FAQ[];
  const faqSchema = faqs && faqs.length > 0 ? generateFaqSchema(faqs) : null;
  const whyHireUsItems = t('homePage.whyHireUsItems', { returnObjects: true }) as { icon: string, title: string, description: string, slogan: string }[];
  const icons: { [key: string]: React.ReactNode } = {
    Shield: <Shield size={24} />,
    Briefcase: <Briefcase size={24} />,
    Coffee: <Coffee size={24} />,
  };


  return (
    <>
      <Seo
        title={t('homePage.seoTitle')}
        description={t('homePage.seoDescription')}
        schema={[breadcrumbSchema, faqSchema].filter(Boolean)}
      />
      <div>
        <Hero />
        <AuthorityBurst stats={firmStats} />

        {/* About Section */}
        <AnimatedSection className="py-24 dark-section-bg">
          <div className="mx-auto max-w-7xl px-6 grid md:grid-cols-2 items-center gap-16">
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-card border border-white/10">
              <img
                src="https://images.pexels.com/photos/8112152/pexels-photo-8112152.jpeg?auto=compress&cs=tinysrgb&w=800&fm=webp&q=75"
                srcSet="https://images.pexels.com/photos/8112152/pexels-photo-8112152.jpeg?auto=compress&cs=tinysrgb&w=400&fm=webp&q=75 400w, https://images.pexels.com/photos/8112152/pexels-photo-8112152.jpeg?auto=compress&cs=tinysrgb&w=800&fm=webp&q=75 800w, https://images.pexels.com/photos/8112152/pexels-photo-8112152.jpeg?auto=compress&cs=tinysrgb&w=1200&fm=webp&q=75 1200w"
                sizes="(max-width: 768px) 100vw, 50vw"
                className="w-full h-full object-cover"
                alt="Lawyers meeting with a client in a modern office"
                loading="lazy"
                width="800"
                height="600"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-transparent to-black/30"></div>
            </div>
            <div className="text-center md:text-left">
              <h2 className="text-4xl md:text-5xl font-serif font-bold">{t('homePage.aboutTitle')}</h2>
              <p className="mt-6 text-lg text-white/90">
                <Trans
                  i18nKey="homePage.aboutParagraph1"
                  t={t}
                  components={{ bold: <strong /> }}
                />
              </p>
                <p className="mt-4 text-lg text-white/90">
                {t('homePage.aboutParagraph2')}
              </p>
              <p className="mt-4 text-lg text-white/90">
                <Trans
                  i18nKey="homePage.aboutParagraph3"
                  t={t}
                  components={{ gold: <span className="gold-gradient-text font-bold" /> }}
                />
              </p>
              <div className="mt-8">
                <Button to="/about" variant="secondary">{t('buttons.learnAboutOurFirm')}</Button>
              </div>
            </div>
          </div>
        </AnimatedSection>
        
        {/* Practice Areas */}
        {practiceAreas.length > 0 && (
          <AnimatedSection className="py-24 bg-white/5">
            <div className="mx-auto max-w-7xl px-6">
              <div className="mx-auto mb-16 max-w-2xl text-center">
                <h2 className="text-4xl md:text-5xl font-serif font-bold">{t('homePage.practiceAreasTitle')}</h2>
                <p className="mt-4 text-lg text-white/80">{t('homePage.practiceAreasSubtitle')}</p>
              </div>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {practiceAreas.slice(0, 6).map((area, index) => (
                  <PracticeAreaCard key={index} area={area} />
                ))}
              </div>
                <div className="mt-16 text-center">
                    <Button to="/practice-areas" variant="secondary">{t('buttons.viewAllPracticeAreas')}</Button>
                </div>
            </div>
          </AnimatedSection>
        )}

        {/* Reviews Section */}
        {testimonials.length > 0 && (
          <AnimatedSection className="relative py-24 overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center bg-fixed"
              style={{ backgroundImage: "url('https://images.pexels.com/photos/8837510/pexels-photo-8837510.jpeg?auto=compress&cs=tinysrgb&w=1600&fm=webp&q=75')" }}
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-brand-dark/90" />
            <div className="relative z-10">
              <div className="mx-auto max-w-7xl px-6">
                <div className="mx-auto mb-16 max-w-3xl text-center">
                  <h2 className="text-4xl md:text-5xl font-serif font-bold drop-shadow-lg">{t('homePage.reviewsTitle')}</h2>
                  <p className="mt-4 text-lg text-white/80">{t('homePage.reviewsSubtitle')}</p>
                </div>
                <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                  {testimonials.map((testimonial, index) => {
                    // Use a different set of results to avoid duplicating the 'Featured Results'
                    const pairedResult = caseResults[index + 3] || caseResults[0]; // Fallback to first if not enough
                    return (
                      <FlippableCard
                        key={index}
                        frontContent={
                          <div className="flex h-full flex-col justify-center rounded-3xl border border-white/10 bg-brand-dark/80 p-8 shadow-xl glass-effect">
                            <p className="text-lg leading-relaxed text-white">“{testimonial.quote}”</p>
                          </div>
                        }
                        backContent={
                          <div className="flex h-full flex-col items-center justify-center rounded-3xl border border-white/10 bg-black/80 p-8 shadow-xl glass-effect text-center">
                            <div className="text-3xl font-bold gold-gradient-text">{pairedResult?.amount || 'Confidential'}</div>
                            <div className="mt-2 text-md text-white/80">{pairedResult?.caseType || 'Successful Settlement'}</div>
                          </div>
                        }
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </AnimatedSection>
        )}


        {/* Results Section */}
        {caseResults.length > 0 && (
          <AnimatedSection className="py-24 dark-section-bg">
            <div className="mx-auto max-w-7xl px-6">
                <div className="mx-auto mb-16 max-w-2xl text-center">
                    <h2 className="text-4xl md:text-5xl font-serif font-bold">{t('homePage.resultsTitle')}</h2>
                    <p className="mt-4 text-lg text-white/80">{t('homePage.resultsSubtitle')}</p>
                </div>
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                    {caseResults.slice(0,3).map((result, index) => (
                        <FlippableCard
                            key={index}
                            frontContent={
                                <div className="h-full rounded-3xl border border-yellow-400/20 bg-gradient-to-br from-yellow-400/10 to-transparent p-8 text-white shadow-card shadow-yellow-500/10 flex flex-col justify-center items-center text-center">
                                    <div className="text-5xl font-extrabold gold-gradient-text">{result?.amount || 'Confidential'}</div>
                                    <div className="mt-3 text-lg tracking-wide text-white/90">{result?.caseType || 'Successful Settlement'}</div>
                                </div>
                            }
                            backContent={
                                <div className="h-full rounded-3xl border border-yellow-400/20 bg-gradient-to-tr from-yellow-400/20 to-brand-dark/90 p-8 text-white shadow-card shadow-yellow-500/10 flex flex-col justify-center items-center text-center">
                                    {result?.practiceAreaSlug && (
                                        <LocalizedLink to={resolvePracticeAreaPath(result.practiceAreaSlug, practiceAreas.map((p) => p.slug))} className="inline-flex items-center gap-2 text-sm font-semibold text-white/90 transition-colors rounded-full px-4 py-2 bg-white/10 border border-white/10 hover:bg-white/20 glass-effect">
                                            <Briefcase size={16} /> {t('aboutPage.proofSeeMethodButton')}
                                        </LocalizedLink>
                                    )}
                                </div>
                            }
                        />
                    ))}
                </div>
                <div className="mt-12 text-center">
                    <Button to="/results" variant="secondary">{t('buttons.viewMoreResults')}</Button>
                    <p className="mt-8 text-center text-xs text-white/60 italic">
                        {t('homePage.resultsDisclaimer')}
                    </p>
                </div>
            </div>
          </AnimatedSection>
        )}

        {/* Why Hire Us Section - Redesigned */}
        <AnimatedSection className="py-24 bg-white/5">
            <div className="mx-auto max-w-7xl px-6">
                <div className="mx-auto mb-16 max-w-3xl text-center">
                    <h2 className="text-4xl md:text-5xl font-serif font-bold">{t('homePage.whyHireUsTitle')}</h2>
                    <p className="mt-4 text-lg text-white/80">{t('homePage.whyHireUsSubtitle')}</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
                    {whyHireUsItems.map((item, index) => (
                        <FlippableCard
                            key={index}
                            className="h-full"
                            frontContent={
                                <div className="p-8 rounded-2xl border border-white/10 bg-brand-dark glass-effect h-full flex flex-col items-center text-center">
                                    <div className="w-16 h-16 rounded-full gold-gradient-bg flex items-center justify-center text-black mb-4 flex-shrink-0">
                                        {icons[item.icon as keyof typeof icons]}
                                    </div>
                                    <h3 className="text-xl font-bold font-serif">{item.title}</h3>
                                    <p className="mt-3 text-white/80 text-sm">{item.description}</p>
                                </div>
                            }
                            backContent={
                                <div className="p-8 rounded-2xl border border-white/10 bg-black/80 glass-effect h-full flex flex-col items-center text-center justify-center">
                                    {React.cloneElement(icons[item.icon as keyof typeof icons] as React.ReactElement, { size: 64, className: 'text-brand-gold-light' })}
                                    <p className="mt-4 text-xl font-serif font-semibold text-white">"{item.slogan}"</p>
                                </div>
                            }
                        />
                    ))}
                </div>
            </div>
        </AnimatedSection>

          {/* CTA Strip */}
          <AnimatedSection className="py-24">
              <div className="mx-auto max-w-7xl px-6">
                  <div className="relative overflow-hidden flex flex-col md:flex-row items-center md:text-left text-center justify-between gap-8 rounded-3xl border border-yellow-400/30 p-12 shadow-card shadow-yellow-400/20"
                        style={{background: 'linear-gradient(145deg, rgba(205,175,42,0.1), rgba(20,20,24,0.9))'}}>
                      <div>
                          <h3 className="text-3xl font-bold font-serif">{t('homePage.ctaTitle')}</h3>
                          <p className="mt-3 text-white/80 text-lg">{t('homePage.ctaSubtitle')}</p>
                      </div>
                      <div className="flex-shrink-0">
                          <Button to="/contact" variant="primary">
                              {t('homePage.ctaButton')}
                          </Button>
                      </div>
                  </div>
              </div>
          </AnimatedSection>

          {/* FAQ Section */}
          {faqs && faqs.length > 0 && (
            <div className="py-24 bg-white/5">
              <FaqSection 
                faqs={faqs}
                title={t('homePage.faqTitle')}
              />
            </div>
          )}

        <PageExtras pageKey="home" />

      </div>
    </>
  );
};

export default HomePage;